import { bcEnums, OperatorInitialGreetingType } from './../common/constants/boldchat-enums';
import { CannedMessageRequest } from './canned-message-request.interface';
import { OperatorInitialGreeting } from '../api/models/operator-initial-greeting';
import { Injectable } from '@angular/core';
import { BcRestApi } from 'common/api/rest-api';
import { UserContextService } from 'common/user-context/user-context.service';
import { CannedMessage } from 'shared/components/canned-message-panel/canned-message.model';
import { LogoutSubscriber } from 'shared/services/subscribers/logout-subscriber.service';
import { cloneDeep } from 'lodash';

type PromiseCache = { [key: string]: Promise<any> };

@Injectable({
  providedIn: 'root'
})
export class BcCannedMessagesRepo {
  public cachedCannedMessage;
  public cannedMessagePromise;
  public chatVariables;
  public translatedCannedMessages;
  public textChatVariablesCache: PromiseCache;
  public textChatCannedMessagesCache: PromiseCache;

  constructor(private bcRestApi: BcRestApi, private userContextService: UserContextService,
              LogoutSubscriber: LogoutSubscriber) {
    this.resetCannedMessages();

    LogoutSubscriber.subscribe(this.resetCannedMessages.bind(this));
  }

  resetCannedMessages() {
    this.textChatVariablesCache = {};
    this.textChatCannedMessagesCache = {};

    this.cachedCannedMessage = {};
    this.cannedMessagePromise = {};
    this.chatVariables = {};
    this.translatedCannedMessages = {};
  }

  getChatVariables(chatId) {
    if (this.chatVariables[chatId]) {
      return this.chatVariables[chatId];
    }

    this.chatVariables[chatId] = this.bcRestApi.getChatCannedMessageVariables(chatId)
      .catch(() => {
        this.chatVariables[chatId] = null;
      });

    return this.chatVariables[chatId];
  }

  getTextChatVariables(textChatId) {
    if (this.textChatVariablesCache[textChatId] == null) {
      this.textChatVariablesCache[textChatId] = this.bcRestApi.getTextChatCannedMsgVariables(textChatId)
        .catch(() => {
          this.textChatVariablesCache[textChatId] = null;
        });
    }
    return this.textChatVariablesCache[textChatId];
  }

  getCannedMessages(request: CannedMessageRequest, replaceVariables?): Promise<any> {
    switch (request && request.type) {
      case bcEnums.cannedMessageTypes.CHAT: {
        return this.getChatCannedMessages(request.chatWindowDefId, request.id, request.language, replaceVariables);
      }
      case bcEnums.cannedMessageTypes.TEXTCHAT: {
        return this.getTextChatCannedMessages(request.textChatId, replaceVariables);
      }
      case bcEnums.cannedMessageTypes.EMAIL: {
        return this._getEmailCannedMessages(request.emailAccountId, request.emailId);
      }
      default: {
        throw new Error(`${request.type} is not a currently known canned message type.`);
      }
    }
  }

  getTextChatCannedMessages(textChatId, replaceVariables): Promise<any> {
    if (this.textChatCannedMessagesCache[textChatId] == null) {
      if (!textChatId) {
        throw new Error('Missing textChatId from canned message request.');
      }

      this.textChatCannedMessagesCache[textChatId] = this.bcRestApi.getTextChatCannedMsgByTextChatId(textChatId).then(cannedMessages => {
        cannedMessages.messages = cannedMessages.messages.filter((message) => {
          return message.type !== bcEnums.interactiveCannedMessageTypes.LIST_PICKER;
        });
        return replaceVariables === false ? cannedMessages : this._replaceCannedMessageVariables(cannedMessages, this.getTextChatVariables(textChatId));
      })
        .catch(() => {
          this.textChatCannedMessagesCache[textChatId] = null;
        });

    }
    return this.textChatCannedMessagesCache[textChatId];
  }

  getChatCannedMessages(chatWindowDefId, chatId, language, replaceVariables): Promise<any> {
    return this.getChatCannedMessagesUnfiltered(chatWindowDefId, chatId, replaceVariables)
      .then(result => {
        if (!language) {
          return result;
        }
        return {
          messages: result.messages.filter(m => !m.language || m.language === language),
          folders: result.folders
        };
      });
  }

  getChatCannedMessagesUnfiltered(chatWindowDefId, chatId, replaceVariables): Promise<any> {
    if (this.cannedMessagePromise[chatWindowDefId]) {
      return this.cannedMessagePromise[chatWindowDefId];
    }

    const ccm = this.cachedCannedMessage[chatWindowDefId];
    if (ccm && ccm.messages && ccm.messages.length > 0) {
      if (this.translatedCannedMessages[chatId]) {
        return this._getCachedCannedMessages(chatWindowDefId, chatId);
      }

      return this._getCachedCannedMessages(chatWindowDefId)
        .then(cannedMessages => {
          this.translatedCannedMessages[chatId] = replaceVariables === false ? cannedMessages : this._replaceCannedMessageVariables(cannedMessages, this.getChatVariables(chatId));
          return this.translatedCannedMessages[chatId];
        });
    }

    this.cannedMessagePromise[chatWindowDefId] = this.bcRestApi.getChatCannedMessages(chatWindowDefId)
      .then(cannedMessages => {
        this.cachedCannedMessage[chatWindowDefId] = cannedMessages;
        return cannedMessages;
      })
      .then(cannedMessages => {
        this.translatedCannedMessages[chatId] = replaceVariables === false ? cannedMessages : this._replaceCannedMessageVariables(cannedMessages, this.getChatVariables(chatId));
        this.cannedMessagePromise[chatWindowDefId] = null;

        return this.translatedCannedMessages[chatId];
      })
      .catch(() => {
        this.cannedMessagePromise[chatWindowDefId] = null;
      });

    return this.cannedMessagePromise[chatWindowDefId];
  }

  private _getEmailCannedMessages(emailAccountId, emailId): Promise<any> {
    if (emailAccountId) {
      return this.bcRestApi.getEmailCannedMsgByEmailAccountId(emailAccountId);
    } else if (emailId) {
      return this.bcRestApi.getEmailCannedMsgByEmailId(emailId);
    }

    throw new Error('Missing emailId or emailAccountId from canned message request.');
  }

  private _replaceCannedMessageVariables(cannedMessages, cmVariablesPromise): Promise<any> {
    const resolvedCannedMessages = cloneDeep(cannedMessages);
    return cmVariablesPromise
      .then(cmVariables => {
        if (cmVariables) {
          this._replaceCannedMessageKnownVariables(resolvedCannedMessages, cmVariables);
        }
        return resolvedCannedMessages;
      });
  }

  private _replaceCannedMessageKnownVariables(cannedMessages, cannedMessageVariables): void {
    cannedMessages.messages
      .filter(msg => msg.text.indexOf(bcEnums.cannedMessageVariable.START) > -1)
      .forEach(msg => {
        const regex = /\$\{(.*?)\}/g;

        let regexMatch;

        while ((regexMatch = regex.exec(msg.text)) !== null) { // eslint-disable-line no-cond-assign
          // This is necessary to avoid infinite loops with zero-width matches
          if (regexMatch.index === regex.lastIndex) {
            regex.lastIndex++;
          }

          // The result can be accessed through the `regexMatch` variable.
          regexMatch.forEach((match, groupIndex) => {
            if (groupIndex === 1) {
              let cmVariableToFind = null;
              const idxOfColon = match.indexOf(':');

              // Get the CM variable name when there is a default value
              if (idxOfColon > -1) {
                cmVariableToFind = match.substring(0, idxOfColon);
              }

              const cmVar = this.getCannedMessageVariable(cannedMessageVariables, cmVariableToFind || match);

              // CM variable with value
              if (cmVar) {
                this.fillInDefaultValue(msg, match, cmVar.variableName, cmVar.variableValue);
              } else {
                // CM variable with default value
                this.fillInDefaultValue(msg, match);
              }
            }
          });
        }

        // Replace leftover CM variables
        msg.text = msg.text.replace(regex, '');
      });
  }

  fillInDefaultValue(msg, matchWithoutCurly, cmVarName?, cmVarValue?) {
    const idxOfColon = matchWithoutCurly.indexOf(':');
    const defaultValue = matchWithoutCurly.substring(idxOfColon + 1);

    let replaceThis;

    if (idxOfColon === -1) {
      // Replace the following: ${chat.ChatName}
      replaceThis = bcEnums.cannedMessageVariable.START + cmVarName + bcEnums.cannedMessageVariable.END;
    } else {
      // Replace the following: ${chat.ChatName:defaultValue}
      if (cmVarName) {
        replaceThis = bcEnums.cannedMessageVariable.START + cmVarName + ':' + defaultValue + bcEnums.cannedMessageVariable.END;
      } else {
        const varName = matchWithoutCurly.substring(0, idxOfColon);
        replaceThis = bcEnums.cannedMessageVariable.START + varName + ':' + defaultValue + bcEnums.cannedMessageVariable.END;
      }
    }

    msg.text = msg.text.replace(replaceThis, cmVarValue || defaultValue);
  }

  getCannedMessageVariable(cannedMessageVariables, name) {
    return cannedMessageVariables.find((cm) => cm.variableName === name);
  }

  private _getCachedCannedMessages(chatWindowDefId, chatId?): Promise<CannedMessage> {
    if (chatId) {
      return Promise.resolve(this.translatedCannedMessages[chatId]);
    } else {
      return Promise.resolve(this.cachedCannedMessage[chatWindowDefId]);
    }
  }

  getCannedMessageNameById(chatId, chatWindowDefId, cannedMessageId, replaceVariables) {
    const id = chatId;
    const type = bcEnums.cannedMessageTypes.CHAT;

    return this.getCannedMessages({ type, id, chatWindowDefId }, replaceVariables)
      .then(cannedMessages => this.filterCannedMessageById(cannedMessages, id));
  }

  getChatWelcomeCannedMessageFromChatId(chatId) {
    return this.bcRestApi.getChat(chatId).then((chat) => {
      const chatWindowDefId = chat.chatWindowDefId;
      const language = chat.language;
      const websiteDefId = chat.websiteDefId;
      return this.getChatWelcomeCannedMessage(chatWindowDefId, chatId, language, websiteDefId);
    });

  }

  getChatWelcomeCannedMessage(chatWindowDefId: string, chatId: string, language: string, websiteDefId: string) {
    return this.bcRestApi.getOperatorInitialGreetingSettings()
      .then(async (operatorInitialGreetingSettings) => {
        let welcomeMessageId;
        if (operatorInitialGreetingSettings && operatorInitialGreetingSettings.operatorInitialGreetingType !== OperatorInitialGreetingType.NONE) {
          await this.bcRestApi.getOperatorInitialGreetings()
            .then((operatorInitialGreetings) => {
              welcomeMessageId = this.getOperatorInitialGreetingByGreetingType(operatorInitialGreetings, operatorInitialGreetingSettings.operatorInitialGreetingType, language, websiteDefId);
            });
        }
        welcomeMessageId = welcomeMessageId || this.userContextService.userContext.operatorContext.chatWelcomeMessageId;
        if (!welcomeMessageId) {
          return Promise.resolve(null);
        }

        return this.getChatCannedMessages(chatWindowDefId, chatId, language, true)
          .then((cannedMessages) => {
            return this.filterCannedMessageById(cannedMessages, welcomeMessageId);
          });
      });
  }

  getOperatorInitialGreetingByGreetingType(operatorInitialGreetings: OperatorInitialGreeting[], operatorInitialGreetingType: OperatorInitialGreetingType, language: string, websiteDefId: string): string {
    if (operatorInitialGreetingType === OperatorInitialGreetingType.LANGUAGE) {
      return this.filterInitialGreetingsByLanguageCode(operatorInitialGreetings, language);
    }
    if (operatorInitialGreetingType === OperatorInitialGreetingType.WEBSITE) {
      return this.filterInitialGreetingsByWebsite(operatorInitialGreetings, websiteDefId);
    }
    return null;
  }

  filterCannedMessageById(cannedMessages, cannedMessageId) {
    const filtered = cannedMessages.messages.filter(item => item.id === cannedMessageId);
    if (filtered.length > 0) {
      return filtered[0];
    }
    return null;
  }

  filterInitialGreetingsByLanguageCode(operatorInitialGreetings: OperatorInitialGreeting[], languageCode: string): string {
    const filtered = operatorInitialGreetings.filter((item: OperatorInitialGreeting) => item.languageCode === languageCode);
    if (filtered.length > 0) {
      return filtered[0].cannedMessageId;
    }
    return null;
  }

  filterInitialGreetingsByWebsite(operatorInitialGreetings: OperatorInitialGreeting[], websiteDefId: string): string {
    const filtered = operatorInitialGreetings.filter((item: OperatorInitialGreeting) => item.websiteDefId === websiteDefId);
    if (filtered.length > 0) {
      return filtered[0].cannedMessageId;
    }
    return null;
  }

  getTextChatWelcomeCannedMessage(textChatId) {
    const welcomeMessageId = this.userContextService.userContext.operatorContext.textChatWelcomeMessageId;

    if (!welcomeMessageId) {
      return Promise.resolve(null);
    }

    return this.bcRestApi.getTextChatCannedMsg(textChatId, welcomeMessageId);
  }
}
